import { Trans, useLingui } from "@lingui/react/macro";
import { Badge, Button, Spinner } from "@rakazo/ui-web";
import { SwitchField } from "@rakazo/ui-web/components/ui/field";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@rakazo/ui-web/components/ui/table";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryUserId } from "../../lib/query";
import { errorText } from "../../lib/user-error";
import { drokRpc } from "../rpc";
import { PublishDialog, type PublishTarget } from "./publish-dialog";

export function templatesKey(userId: string | null, includeArchived: boolean) {
  return ["drok", `user:${userId ?? ""}`, "admin.templates", includeArchived] as const;
}

/** Every published template, with its latest version and how many employees have it. */
export function AdminTemplatesListPage() {
  const { t } = useLingui();
  const navigate = useNavigate();
  const selfId = useQueryUserId();
  const [includeArchived, setIncludeArchived] = useState(false);
  const [publishing, setPublishing] = useState<PublishTarget | null>(null);
  const query = useQuery({
    queryKey: templatesKey(selfId, includeArchived),
    queryFn: () => drokRpc.templates.list({ includeArchived }),
    enabled: selfId !== null,
  });
  const templates = query.data ?? [];
  return (
    <section data-testid="admin-templates">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <SwitchField
          label={<Trans>Show archived</Trans>}
          checked={includeArchived}
          onCheckedChange={setIncludeArchived}
        />
        <Button onClick={() => setPublishing({ kind: "new" })}>
          <Trans>Publish a template</Trans>
        </Button>
      </div>
      {query.isPending ? (
        <div className="grid place-items-center p-8">
          <Spinner aria-label={t`Loading…`} />
        </div>
      ) : query.isError ? (
        <div role="alert" className="rounded-xl bg-muted/50 p-4 text-sm">
          <p className="text-destructive">{errorText(query.error)}</p>
          <Button className="mt-2" size="sm" variant="outline" onClick={() => void query.refetch()}>
            <Trans>Try again</Trans>
          </Button>
        </div>
      ) : templates.length === 0 ? (
        <p className="rounded-xl bg-muted/50 p-4 text-sm text-muted-foreground">
          <Trans>No templates yet</Trans>
        </p>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  <Trans>Template</Trans>
                </TableHead>
                <TableHead>
                  <Trans>Version</Trans>
                </TableHead>
                <TableHead>
                  <Trans>Assigned</Trans>
                </TableHead>
                <TableHead className="text-end" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {templates.map((template) => (
                <TableRow key={template.id} data-testid="admin-template-row">
                  <TableCell>
                    <span className="font-medium">{template.name}</span>
                    {template.archivedAt ? (
                      <Badge variant="outline" className="ms-2">
                        <Trans>Archived</Trans>
                      </Badge>
                    ) : null}
                  </TableCell>
                  <TableCell>v{template.latestVersion}</TableCell>
                  <TableCell>
                    <Trans>
                      {template.activeAssignments} active, {template.copies} copies
                    </Trans>
                  </TableCell>
                  <TableCell className="text-end">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate(`/admin/templates/${template.id}`)}
                    >
                      <Trans>Open</Trans>
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <PublishDialog
        target={publishing}
        onClose={() => setPublishing(null)}
        onPublished={(id) => navigate(`/admin/templates/${id}`)}
      />
    </section>
  );
}
